import { useCallback } from 'react';
import monitoring from '@clearscore-group/lib.helpers.monitoring';

import { executeWhenCookiesAccepted } from './lib/cookie-banner';
import { getSanitisedUrl } from './lib/helpers';
import useDefaultSegmentProps from './lib/hooks/use-default-segment-props';

/**
 * Get the default segment props that must be generated at event send time
 */
const getWindowSegmentProps = () => {
    const { url } = getSanitisedUrl();

    return {
        location: url,
        referrer: document.referrer || 'Direct',
    };
};

const useTracking = () => {
    const defaultSegmentProps = useDefaultSegmentProps();

    const track = useCallback(
        ({ name, props = {}, event = 'track', onComplete } = {}) => {
            if (monitoring.hasMonitoring()) {
                monitoring.log(`Cannot send tracking event ${name} - no window.analytics available`);
            }

            let hasFiredOnComplete = false;

            const fallbackTimeout = setTimeout(() => {
                if (onComplete && !hasFiredOnComplete) onComplete();
            }, 100);

            const customOnComplete = () => {
                hasFiredOnComplete = true;
                clearTimeout(fallbackTimeout);
                onComplete();
            };

            executeWhenCookiesAccepted(() => {
                const hasTracking = typeof window?.analytics?.track === 'function';

                if (!hasTracking) {
                    monitoring.log(`Cannot send tracking event ${name} - no window.analytics available`);
                    return;
                }

                window.analytics[event](
                    name,
                    {
                        ...getWindowSegmentProps(),
                        ...defaultSegmentProps,
                        ...props,
                    },
                    // analytics.js "options" object - not needed for us (yet!)
                    {},
                    // An optional function called when the analytics request completes
                    onComplete ? customOnComplete : undefined,
                );
            });
        },
        [defaultSegmentProps],
    );

    return track;
};

export default useTracking;
